import React from 'react';

import { ScrollView, addWithConfig } from '@rbilabs/universal-components';

import ActionButton from 'components/action-button';
import { ProductCardListSkeleton } from 'components/product/skeleton';
import { CenteredRowContainer } from 'components/ui/centered-row-container';
import useEffectOnUpdates from 'hooks/use-effect-on-updates';
import { useNativeTabBarContentInset } from 'hooks/use-native-tab-bar-content-inset';
import { UserOrdersDefaultLimit } from 'hooks/use-user-orders';
import { useAuthContext } from 'state/auth';
import { t } from 'state/intl/translations';
import { useOrderContext } from 'state/order';

import {
  ErrorState,
  LoggedOutEmptyState,
  NoRecentOrdersEmptyState,
} from '../recent-order-card-list/empty-state';

import MinimalRecentOrderCardGroup from './minimal-recent-order-card-group';
import { useMinimalRecentItems } from './use-minimal-recent-items';

const LoadMoreButton = addWithConfig(ActionButton).withConfig({
  style: {
    marginLeft: '30%',
    width: '40%',
    marginBottom: '10%',
  },
});

const MinimalRecentItemsCardList: React.FC = () => {
  const nativeTabBarContentInset = useNativeTabBarContentInset();
  const { isAuthenticated } = useAuthContext();
  const { serverOrder } = useOrderContext();
  const {
    orders,
    count,
    isLoading,
    error,
    fetchingMore,
    handleLoadMore,
    refetch,
    resumeAfter,
  } = useMinimalRecentItems(
    true,
    {
      limit: UserOrdersDefaultLimit,
    },
    'no-cache'
  );

  const rbiOrderId = serverOrder?.rbiOrderId;
  useEffectOnUpdates(() => {
    refetch();
  }, [refetch, rbiOrderId]);

  if (isLoading && !fetchingMore) {
    return (
      <CenteredRowContainer>
        <ProductCardListSkeleton />
      </CenteredRowContainer>
    );
  }

  if (!isAuthenticated) {
    return (
      <ScrollView contentContainerStyle={{ paddingBottom: nativeTabBarContentInset }}>
        <CenteredRowContainer>
          <LoggedOutEmptyState />
        </CenteredRowContainer>
      </ScrollView>
    );
  }

  if (error) {
    return (
      <ScrollView contentContainerStyle={{ paddingBottom: nativeTabBarContentInset }}>
        <CenteredRowContainer>
          <ErrorState onRefresh={() => refetch()} />
        </CenteredRowContainer>
      </ScrollView>
    );
  }

  if (!orders.length && !resumeAfter) {
    return (
      <ScrollView contentContainerStyle={{ paddingBottom: nativeTabBarContentInset }}>
        <CenteredRowContainer>
          <NoRecentOrdersEmptyState />
        </CenteredRowContainer>
      </ScrollView>
    );
  }

  return (
    <ScrollView
      showsVerticalScrollIndicator={false}
      accessibilityLabel="recent-order-card-list-container"
      keyboardShouldPersistTaps="handled"
      contentContainerStyle={{ paddingBottom: nativeTabBarContentInset }}
    >
      <CenteredRowContainer>
        {orders.map(order => (
          <MinimalRecentOrderCardGroup key={order.rbiOrderId} order={order} />
        ))}
        {count > 0 && resumeAfter && (
          <LoadMoreButton
            testID="load-more-button"
            onPress={handleLoadMore}
            isLoading={fetchingMore}
          >
            {t('loadMore')}
          </LoadMoreButton>
        )}
      </CenteredRowContainer>
    </ScrollView>
  );
};

export default MinimalRecentItemsCardList;
